import { supabase } from "./supabase.js";

// The Supabase boundary for groups. Unlike items (keyed by source + external
// id), a group is purely user-authored, so it's keyed by its own uuid — the same
// id travels across devices. Membership/child arrays reference item and group
// ids, which are also stable across devices (items sync with their local uuid as
// the row id), so a synced hierarchy resolves on every device.
//
// Groups carry no status of their own (they're member overviews), so the row is
// just structure: name, scope, pin, membership, nesting, cover, ordering. The
// retired auto-group fields (kind/auto_rule/auto_key) are not synced; restored
// groups are always plain manual groups.

export function toCloudGroup(record, userId) {
	return {
		id: record.id,
		user_id: userId,
		name: record.name,
		description: record.description ?? null,
		media_type: record.media_type ?? null,
		pinned_to_home: !!record.pinned_to_home,
		manual_member_ids: record.manual_member_ids ?? [],
		manual_exclude_ids: record.manual_exclude_ids ?? [],
		child_group_ids: record.child_group_ids ?? [],
		cover_item_id: record.cover_item_id ?? null,
		is_ordered: !!record.is_ordered,
		ordered_ids: record.ordered_ids ?? null,
		// Only when set: older databases lack the column (see upsert fallback).
		...(record.series_ref ? { series_ref: record.series_ref } : {}),
		...(record.smart_rule ? { smart_rule: record.smart_rule } : {}),
		...(record.share_token ? { share_token: record.share_token } : {}),
		created_at: record.created_at,
		updated_at: record.updated_at,
	};
}

// Cloud row → local group record. `_synced` tells the reconciler this group is
// cloud-backed (and may be removed locally if it later vanishes server-side).
export function fromCloudGroup(row) {
	return {
		id: row.id,
		name: row.name,
		description: row.description ?? null,
		// Auto-grouping is retired; a cloud group is always a plain manual group.
		kind: "manual",
		auto_rule: null,
		auto_key: null,
		media_type: row.media_type ?? null,
		pinned_to_home: !!row.pinned_to_home,
		manual_member_ids: row.manual_member_ids ?? [],
		manual_exclude_ids: row.manual_exclude_ids ?? [],
		child_group_ids: row.child_group_ids ?? [],
		cover_item_id: row.cover_item_id ?? null,
		is_ordered: !!row.is_ordered,
		ordered_ids: row.ordered_ids ?? null,
		series_ref: row.series_ref ?? null,
		smart_rule: row.smart_rule ?? null,
		share_token: row.share_token ?? null,
		created_at: row.created_at,
		updated_at: row.updated_at,
		_synced: true,
	};
}

// ---------- remote calls ----------

// Newer optional columns (series_ref: migrations/2026-10-15-follows.sql;
// smart_rule + share_token: 2026-10-16-collections.sql). Until the owner adds
// one, a row carrying it is rejected ("Could not find the 'x' column"): retry
// without it (the value stays on this device) and stop sending it this session.
const OPTIONAL_COLUMNS = ["series_ref", "smart_rule", "share_token"];
const missingColumns = new Set();
const stripMissing = (row) => {
	const out = { ...row };
	for (const c of missingColumns) delete out[c];
	return out;
};

async function upsertGroupRows(rows) {
	const { error } = await supabase.from("groups").upsert(rows.map(stripMissing), { onConflict: "id" });
	if (!error) return;
	const col = OPTIONAL_COLUMNS.find((c) => !missingColumns.has(c) && new RegExp(c).test(error.message ?? ""));
	if (col) {
		missingColumns.add(col);
		return upsertGroupRows(rows);
	}
	throw error;
}

export async function upsertCloudGroup(row) {
	await upsertGroupRows([row]);
}

// Batch variant for the one-time local→cloud migration.
export async function upsertCloudGroups(rows) {
	if (!rows.length) return;
	await upsertGroupRows(rows);
}

export async function deleteCloudGroup(userId, id) {
	const { error } = await supabase
		.from("groups")
		.delete()
		.match({ user_id: userId, id });
	if (error) throw error;
}

export async function fetchCloudGroups(userId) {
	const { data, error } = await supabase
		.from("groups")
		.select("*")
		.eq("user_id", userId);
	if (error) throw error;
	return data ?? [];
}
